/**
 * Rollout switch for "break on top of the estimated drive duration" in PLANNING, for REGULAR
 * (non-fixed-time) assignments.
 *
 * - BEFORE the configured date (by the assignment's BookedTo): regular assignments are planned the old way —
 *   EstimatedDuration is the pure drive estimate and the break is NOT added to the planned/occupied time.
 * - ON/AFTER the configured date: the planned/occupied time = drive + break (the picker still edits the pure
 *   drive estimate; the stored EstimatedDuration becomes drive + break, so "Estimerad tid" and the reserved
 *   scheduling window include the break).
 *
 * The date is the global setting `GlobalSettingEnum.BreakAddedToEstimatedDurationFromDate`, editable by admins
 * at /settings/globala-installningar. `useBreakOnTopPlanningSetting()` keeps the module-level value below in
 * sync with that setting (mounted in DetailedCaseFormProvider, which wraps the whole case-detail tree where
 * the planning conversion happens). When unset/empty, the feature is OFF (legacy planning) for everyone.
 *
 * Gating on the assignment's BookedTo (rather than "today") keeps the meaning of each stored value stable and
 * gives admins a clean boundary: "from <this date>, planned times include the break".
 *
 * NOTE: this does NOT affect salary. Salary for regular assignments always deducts the break from the actual
 * clocked time; fixed-time salary is the fixed time. See SalaryTimeHelper (backend).
 *
 * Passenger legs (personal transport or a Samåkning case) are exempt: the field tester rides along and takes the
 * break during the trip, so the break lies INSIDE the leg's time and never extends it — see
 * `breakLiesInsideLeg` / `isBreakOnTopForLeg` below.
 */

import { AssignmentTypeEnum, CaseTypeEnum } from 'api/model';
import { personalTransportAssignmentTypes } from 'constants/AppConstants';

let configuredFromDate: Date | null = null;

/**
 * Sets the rollout date from the raw global-setting string (yyyy-MM-dd). Empty/invalid → feature off.
 * Called by useBreakOnTopPlanningSetting when the global settings load/change.
 */
export const setBreakOnTopPlanningFromDate = (
  rawValue: string | null | undefined
): void => {
  if (!rawValue) {
    configuredFromDate = null;
    return;
  }
  const parsed = new Date(rawValue);
  configuredFromDate = Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** The currently configured rollout date (or null when the feature is off). Exposed for tests/debugging. */
export const getBreakOnTopPlanningFromDate = (): Date | null => configuredFromDate;

/**
 * Whether the "break on top" planning rule applies to a regular assignment with the given BookedTo.
 * Returns false when no rollout date is configured or the date is missing/invalid (→ legacy behaviour).
 */
export const isBreakOnTopInPlanning = (
  bookedTo: Date | string | undefined | null
): boolean => {
  if (!configuredFromDate || !bookedTo) {
    return false;
  }
  const date = bookedTo instanceof Date ? bookedTo : new Date(bookedTo);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  return date.getTime() >= configuredFromDate.getTime();
};

/**
 * Passenger leg: personal transport (train, bus, plane, boat, taxi, public transport) or a Samåkning case. The
 * field tester rides along and takes the break during the trip, so the break lies inside the leg's time — a
 * one-hour train ride with a 30 min break is still one hour. Mirror of the backend's
 * `AssignmentHelper.CarriesBreakTimeAsPassenger`.
 */
export const breakLiesInsideLeg = (
  assignmentTypeID: AssignmentTypeEnum | null | undefined,
  caseTypeID: CaseTypeEnum | null | undefined
): boolean =>
  (assignmentTypeID != null &&
    personalTransportAssignmentTypes.includes(assignmentTypeID)) ||
  caseTypeID === CaseTypeEnum.Samakning;

/**
 * `isBreakOnTopInPlanning` for a specific leg: the rollout date applies, and the leg is not a passenger leg
 * (whose break never goes on top, see `breakLiesInsideLeg`).
 */
export const isBreakOnTopForLeg = (
  bookedTo: Date | string | undefined | null,
  assignmentTypeID: AssignmentTypeEnum | null | undefined,
  caseTypeID: CaseTypeEnum | null | undefined
): boolean =>
  isBreakOnTopInPlanning(bookedTo) &&
  !breakLiesInsideLeg(assignmentTypeID, caseTypeID);
